import { addToast } from "components/ToastContainer/ToastStore";
import { apiUrls } from "constants/apiUrls";
import { routeConstants } from "constants/routeConstants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import {
    useGetPhysicalDetailsQuery,
    useGetProfileStrengthQuery,
    useSetPhysicalDetailsMutation,
} from "slice/user";
import { PhysicalDetails } from "./types";

export const usePhysicalDetails = () => {
    const { user_id } = useAppSelector((state) => state.userAuth),
        {
            data: physicalDetailsGetResponse,
            isFetching,
            refetch,
        } = useGetPhysicalDetailsQuery(user_id),
        [
            setPhysicalDetails,
            {
                data: physicalDetailsCreateUpdateResponse,
                isLoading,
                isSuccess,
                isError,
            },
        ] = useSetPhysicalDetailsMutation(),
        [isModalOpen, setIsModalOpen] = useState(false),
        [inputs, setInputs] = useState<PhysicalDetails>({
            weight: "",
            height: "",
            hips: "",
            waist: "",
            bust: "",
            eye_color: "",
            hair_color: "",
            hair_length: "",
            piercings: "Yes",
            ethnicity: "",
            tattoos: "",
        }),
        [httpMethod, setHttpMethod] = useState("POST"),
        [clickedOnNext, setClickedOnNext] = useState(false);
    const { refetch: refetchProfileStrength } = useGetProfileStrengthQuery(
        user_id ?? "",
        {
            skip: !user_id,
        }
    );
    const navigateTo = useNavigate();
    const toggleModal = () => setIsModalOpen((prev) => !prev);

    const handleInputChange = useCallback((e: any) => {
        const { name, value } = e.target;
        setInputs((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    }, []);

    const handleSelectChange = useCallback((name: string, value: string) => {
        setInputs((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    }, []);

    const isSubmitDisabled = useMemo(() => {
        if (Object.values(inputs).every((v) => Boolean(v))) return false;
        else return true;
    }, [inputs]);

    const onSubmit = useCallback(() => {
        setPhysicalDetails({
            user_id,
            method: httpMethod,
            url:
                httpMethod === "POST"
                    ? apiUrls.physicalDetails
                    : apiUrls.physicalDetails + `/${user_id}`,
            ...inputs,
        }).then(() => {
            refetchProfileStrength();
        });
    }, [httpMethod, inputs, setPhysicalDetails, user_id]);

    const handleBack = () => {
        navigateTo(
            routeConstants.profile.editProfile.root +
                routeConstants.profile.editProfile.photosAndVideos
        );
    };

    const handleSaveAndNext = () => {
        setClickedOnNext(true);
        onSubmit();
    };

    useEffect(() => {
        refetch();
    }, [refetch]);

    useEffect(() => {
        if (physicalDetailsCreateUpdateResponse) {
            setHttpMethod("PUT");
        }
    }, [physicalDetailsCreateUpdateResponse]);

    useEffect(() => {
        if (isSuccess) {
            addToast({
                type: "success",
                text: "Physical details saved successfully!",
            });

            if (clickedOnNext) {
                setClickedOnNext(false);
                navigateTo(
                    routeConstants.profile.editProfile.root +
                        routeConstants.profile.editProfile.experienceDetails
                );
            }
        }
    }, [isSuccess, clickedOnNext, navigateTo]);
    useEffect(() => {
        if (isError) {
            addToast({
                type: "error",
                text: "Something went wrong. Please try again later.",
            });
        }
    }, [isError]);

    useEffect(() => {
        if (physicalDetailsGetResponse) {
            setInputs({
                weight: physicalDetailsGetResponse.waist,
                height: physicalDetailsGetResponse.height,
                hips: physicalDetailsGetResponse.hips,
                waist: physicalDetailsGetResponse.waist,
                bust: physicalDetailsGetResponse.bust,
                eye_color: physicalDetailsGetResponse.eye_color,
                hair_color: physicalDetailsGetResponse.hair_color,
                hair_length: physicalDetailsGetResponse.hair_length,
                piercings: physicalDetailsGetResponse.piercings,
                // genres: physicalDetailsGetResponse.genres,
                ethnicity: physicalDetailsGetResponse.ethnicity,
                tattoos: physicalDetailsGetResponse.tattoos,
            });
            if (
                !Object.values(physicalDetailsGetResponse).every(
                    (val) => val === null
                )
            ) {
                setHttpMethod("PUT");
            }
        }
    }, [physicalDetailsGetResponse]);

    return {
        inputs,
        handleInputChange,
        handleSelectChange,
        onSubmit,
        isLoading,
        isFetching,
        isSubmitDisabled,
        handleBack,
        clickedOnNext,
        handleSaveAndNext,
        isModalOpen,
        toggleModal,
    };
};
